import { useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2, Check, Timer } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { confetti, playFanfare } from "@/lib/celebrate";
import { useDrop, useDropCountdown, markClaimed, refreshDrop, mmss, DROP_GRAD } from "@/lib/drops";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function useClaim() {
  const { refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [won, setWon] = useState(null);
  const claim = async (drop) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/drops/${drop.id}/claim`);
      markClaimed(data.claims);
      setWon(data);
      playFanfare();
      confetti({ count: 70, colors: ["#f43f7e", "#fbbf24", "#fde68a", "#ffffff"] });
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Das hat nicht geklappt");
      refreshDrop();
    } finally { setBusy(false); }
  };
  return { busy, won, claim, close: () => setWon(null) };
}

export function DropWonDialog({ won, onClose }) {
  if (!won) return null;
  const b = won.badge;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm rounded-[2rem] text-center p-7 border-0" data-testid="drop-won-dialog">
        <div className="relative mx-auto w-32 h-32">
          <span className="drop-halo absolute inset-0 rounded-full" />
          <span className={`drop-medal relative w-32 h-32 rounded-full bg-gradient-to-br ${DROP_GRAD[b.color] || DROP_GRAD.rose} flex items-center justify-center text-6xl ring-4 ring-white shadow-2xl`}>{b.emoji}</span>
        </div>
        <DialogTitle className="font-display text-2xl font-black mt-2">Geschnappt!</DialogTitle>
        <DialogDescription className="text-sm text-slate-600">Das limitierte Abzeichen <b className="text-slate-900">„{b.name}“</b> gehört jetzt dir und ist für immer in deinem Profil zu sehen.</DialogDescription>
        {won.points > 0 && <p className="mx-auto inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-gradient-to-r from-amber-200 to-amber-400 text-[#4a2a08] font-extrabold" data-testid="drop-won-points">+{won.points} Punkte</p>}
        <button onClick={onClose} data-testid="drop-won-close" className="tap w-full h-12 rounded-full pink-gradient text-white font-extrabold mt-1">Super!</button>
      </DialogContent>
    </Dialog>
  );
}

// big card at the top of the feed
export function DropFeedCard() {
  const s = useDrop();
  const left = useDropCountdown(s);
  const { busy, won, claim, close } = useClaim();
  const d = s.drop;
  if (!d || (!left && !won)) return <DropWonDialog won={won} onClose={close} />;
  const grad = DROP_GRAD[d.color] || DROP_GRAD.rose;
  return (
    <>
      <div className="drop-card relative overflow-hidden rounded-3xl p-4 sm:p-5 flex items-center gap-4" data-testid="drop-feed-card">
        <span className="drop-shine" aria-hidden />
        <span className={`drop-medal relative shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center text-4xl ring-2 ring-white shadow-lg`}>{d.emoji}</span>
        <div className="relative flex-1 min-w-0">
          <p className="text-[11px] font-extrabold uppercase tracking-[.18em] text-rose-600 flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Überraschungs-Drop</p>
          <p className="font-display text-base sm:text-lg font-black leading-tight text-slate-900 break-words" data-testid="drop-feed-name">{d.name}</p>
          <p className="text-xs font-semibold text-slate-600 mt-0.5">Limitiertes Abzeichen{d.points > 0 ? ` + ${d.points} Punkte` : ""} · <span className="inline-flex items-center gap-0.5 tabular-nums"><Timer className="w-3 h-3" /> noch {mmss(left)}</span></p>
        </div>
        {d.claimed ? (
          <span className="relative shrink-0 inline-flex items-center gap-1 px-3 h-10 rounded-full bg-emerald-50 text-emerald-700 text-xs font-extrabold ring-1 ring-emerald-200" data-testid="drop-feed-claimed"><Check className="w-4 h-4" /> Dabei</span>
        ) : (
          <button onClick={() => claim(d)} disabled={busy} data-testid="drop-feed-claim" className="tap relative shrink-0 px-4 h-11 rounded-full pink-gradient text-white text-sm font-extrabold shadow-lg shadow-rose-300 inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Schnappen
          </button>
        )}
      </div>
      <DropWonDialog won={won} onClose={close} />
    </>
  );
}

// small floating bubble on every page
export function DropBubble() {
  const s = useDrop();
  const left = useDropCountdown(s);
  const { busy, won, claim, close } = useClaim();
  const d = s.drop;
  return (
    <>
      {d && !d.claimed && left > 0 && (
        <button onClick={() => claim(d)} disabled={busy} data-testid="drop-bubble" aria-label={`Überraschungs-Drop schnappen, noch ${mmss(left)}`}
          className="drop-bubble tap fixed left-4 bottom-[calc(env(safe-area-inset-bottom)+6.5rem)] lg:bottom-5 z-[45] flex items-center gap-2 pl-1.5 pr-4 h-12 rounded-full bg-white ring-2 ring-rose-200 shadow-xl shadow-rose-300/60">
          <span className={`w-9 h-9 rounded-full bg-gradient-to-br ${DROP_GRAD[d.color] || DROP_GRAD.rose} flex items-center justify-center text-xl`}>{busy ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : d.emoji}</span>
          <span className="text-left leading-none"><span className="block text-[11px] font-extrabold text-rose-600">Drop!</span><span className="block text-xs font-black tabular-nums text-slate-800">{mmss(left)}</span></span>
        </button>
      )}
      <DropWonDialog won={won} onClose={close} />
    </>
  );
}

export function DropBadges({ badges = [], testId = "drop-badges" }) {
  if (!badges?.length) return null;
  return (
    <>
      {[...badges].reverse().map((b) => (
        <span key={b.id} title={`Limitiertes Drop-Abzeichen: ${b.name}`} data-testid={`${testId}-${b.id}`}
          className={`climber-badge inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-white bg-gradient-to-r ${DROP_GRAD[b.color] || DROP_GRAD.rose} shadow-md`}>
          {b.emoji} {b.name}
        </span>
      ))}
    </>
  );
}
